import {
  ACCOUNT_STATUS_SOURCE,
  CAMPAIGN_MESSAGE_STATUS_TYPE,
  CAMPAIGN_STATUS_TYPE,
  CHAT_ROLE,
  DEAL_TYPES,
  EVENT_TYPES,
  HttpStatus,
  INVOICE_STATUS,
  INVOICE_TERMS,
  NOTIFICATION_STATUSES,
  NOTIFICATION_TYPE,
  ORDER_STATUS,
  PERMISSION_KEYS,
  ProductType,
  ROLE_MODULE,
  STATUS,
  STATUS_TYPE,
} from 'utils/constants';

export interface Plan {
  _id: string;
  accountId: string;
  accountUserId: string;
  maxUsers: number;
  currentUsers: number;
  expiresAt: string;
  status: string;
  isActive: string;
  stripeCustomerId: string;
  stripeSubscriptionId: string;
  stripePlan?: {
    verificationLink?: {
      url?: string;
    };
    requirements?: {
      currently_due?: string[];
    };
  };
}

export interface Feature {
  _id: string;
  name: string;
  key: string;
  description: string;
  status: string;
}

export interface Referral {
  _id: string;
  accountId: string;
  referralCode: string;
  referrals: { accountId: string; status: STATUS_TYPE; subscribedAt: string }[];
  balance: number;
}

export interface Account {
  _id: string;
  id: string;
  accountUserId: string;
  accountUsers?: User[];
  accountUser: User;
  activeEmails?: {
    isVerified?: boolean;
    emailAddress?: string;
    source?: string;
  }[];
  activeNumber?: string;
  address?: string;
  companyName?: string;
  contactNumber?: string;
  isOnboarded: boolean;
  billing?: {
    address?: string;
    contactNumber?: string;
    emailAddress?: string;
  };
  plans: Plan;
  createdAt: string;
  updatedAt: string;
  features: Feature;
  featureId: string;
  accountReferral: Referral;
  orgSmsMobileNumber?: string;
  city?: string;
  country?: string;
  state?: string;
  zipCode?: string;
  companyAddressLine1?: string;
  companyAddressLine2?: string;
  photoUrl?: string;
}

export interface Meta {
  page: number;
  total: number;
  totalPages: number;
  perPage: number;
  [key: string]: any;
}

export interface Notification {
  _id: string;
  status: NOTIFICATION_STATUSES;
  actor: {
    meta?: User;
    role: string;
    actorId: string;
  };
  meta?: Record<string, any>;
  createdAt: string;
  type: NOTIFICATION_TYPE;
  userId: string;
  updatedAt: string;
  referenceId: string;
  source: string;
  action: string;
}

export interface Company {
  _id: string;
  emailAddress?: string;
  name: string;
  type: string;
  industry: string;
  photoUrl?: string;
  city: string;
  state: string;
  postalCode: string;
  noEmployees?: number;
  annualRevenue?: number;
  timezone?: string;
  description?: string;
  linkedinUrl?: string;
  companyWebsite?: string;
  isDeletable?: boolean;
  tagId?: Tag[];
  data?: { dataId: string; value: any }[];
  dataList?: Pick<Data, '_id' | 'description' | 'meta' | 'name' | 'type'>[];
  [key: string]: any;
}

export interface AccountStatus {
  _id: string;
  accountId: string;
  name: string;
  key: string;
  description?: string;
  source: ACCOUNT_STATUS_SOURCE;
  meta: { color: string; sequence: number };
}

export interface Lead {
  _id: string;
  id: string;
  createdAt: string;
  updatedAt: string;
  accountId: string;
  jobTitle?: string;
  accountUserId?: User[];
  companyId?: string | Company;
  fullName: string;
  firstName: string;
  middleName?: string;
  lastName?: string;
  mobileNumber?: string;
  fax?: string;
  emailAddress: string;
  photoUrl?: string;
  contactDetails?: any;
  businessNumber?: string;
  website?: string;
  country?: string;
  state?: string;
  city?: string;
  streetAddress1?: string;
  streetAddress2?: string;
  zipcode?: string;
  tagId?: Tag[];
  isDeletable?: boolean;
  data?: { dataId: string; value: any }[];
  dataList?: Pick<Data, '_id' | 'description' | 'meta' | 'name' | 'type'>[];
  accountStatusId?: string | AccountStatus;
}

export type SimpleLead = Pick<
  Lead,
  | '_id'
  | 'accountId'
  | 'companyId'
  | 'jobTitle'
  | 'firstName'
  | 'lastName'
  | 'fullName'
  | 'emailAddress'
  | 'mobileNumber'
  | 'photoUrl'
>;

export interface User {
  _id: string;
  accountId: string;
  fullName: string;
  firstName: string;
  lastName: string;
  emailAddress: string;
  photoUrl?: string;
  status: string;
  roleId: Role;
}

export interface MFA {
  recoveryMobileNumber?: {
    pref: string;
    suff: string;
  };
  emailAddress?: {
    pref: string;
    suff: string;
    client: string;
  };
}

export interface Attachment {
  _id?: string;
  fileUrl: string;
  fileName: string;
  fileSize: number;
  size?: number;
  fileType: string;
  fileThumbnail?: string;
  source: string;
}

export enum COMMENT_TYPE {
  GENERAL = 'GENERAL',
  CALL = 'CALL',
  SMS = 'SMS',
  EMAIL = 'EMAIL',
}
export interface Comment {
  _id: string;
  id: string;
  userId?: User;
  parentId?: string;
  referenceId: string;
  actor?: any;
  source: string;
  content: string;
  createdAt: string;
  updatedAt: string;
  attachments?: Attachment[];
  comments?: {
    total: number;
  };
  type: COMMENT_TYPE;
  logCreatedAt: string;
}

export interface Deal {
  pipelineId: Pipeline | string;
  stageId: string | Stage;
  accountId: string;
  accountUserId: User[] | string[];
  leadId: SimpleLead[] | string[];
  companyId: Company;
  name: string;
  type: keyof typeof DEAL_TYPES;
  closedAt: Date;
  amount: number;
  sequence: number;
  _id: string;
  tagId?: Tag[];
  data?: { dataId: string; value: any }[];
  dataList?: Pick<Data, '_id' | 'description' | 'meta' | 'name' | 'type'>[];
}

export type Pipeline = {
  _id: string;
  id: string;
  accountId: string;
  name: string;
  sequence: number;
  isPrimary: boolean;
  deal: number;
  stage?: Stage[];
};

export type Stage = {
  _id: string;
  pipelineId: string;
  sequence: number;
  name: string;
  actions: string[];
  deal: number;
  isPrimary: boolean;
};

export interface Product {
  _id: string;
  accountId?: string;
  categoryId?: string;
  name: string;
  price: number;
  description?: string;
  type: ProductType;
}

export interface ProductWithQuantity extends Product {
  quantity: number;
}

export interface Order {
  _id: string;
  status: INVOICE_STATUS;
  subtotal: number;
  issuedAt: string;
  dueAt?: string;
  orderNumber: string;
  leadId: string;
  lead: Lead;
  invoiceId: string;
  items: Product[];
  note?: {
    lead?: string;
    account?: string;
  };
  email?: {
    receipt?: {
      subject: string;
    };
  };
  paidAt?: string;
  attachments?: Attachment[];
  invoice: {
    terms: INVOICE_TERMS;
    status: ORDER_STATUS;
  };
}

export interface Invoice {
  _id: string;
}

export type Task = {
  _id: string;
  lead: Lead;
  leadId: string;
  priority: string;
  type?: string;
  taskType?: string;
  title: string;
  status: string;
  dueAt?: string;
  content: string;
  tagId?: Tag[];
  dealId?: string;
  accountUsers: Array<{
    accountId: string;
    createdAt: string;
    emailAddress: string;
    firstName: string;
    fullName: string;
    isEmailAddressVerified: boolean;
    isMfaEnabled: boolean;
    landlineNumber: string;
    lastName: string;
    password: string;
    passwordChangedAt: string;
    phoneNumber: string;
    photoUrl: string;
    recoveryMobileNumber: string;
    roleId: string;
    source: string;
    status: string;
    trashed: boolean;
    trashedAt: string;
    updatedAt: string;
  }>;
  [key: string]: any;
};

export type Guest = {
  emailAddress: string;
  contactNumber?: string;
  firstName?: string;
  lastName?: string;
  fullName?: string;
};

export type Event = {
  _id: string;
  id: string;
  accountUserId: {
    accountId: string;
    createdAt: string;
    emailAddress: string;
    firstName: string;
    fullName: string;
    isEmailAddressVerified: boolean;
    isMfaEnabled: boolean;
    landlineNumber: string;
    lastName: string;
    password: string;
    passwordChangedAt: string;
    phoneNumber: string;
    photoUrl: string;
    recoveryMobileNumber: string;
    roleId: string;
    source: string;
    status: string;
    trashed: boolean;
    trashedAt: string;
    updatedAt: string;
  };
  accountId: string;
  type: EVENT_TYPES;
  entryType?: string;
  title: string;
  eventUrl?: string;
  startAt: string;
  endAt: string;
  dealId?: string;
  content?: string;
  guests?: Guest[];
  notifyBefore?: number;
  location?: string;
  tags?: string[];
  actor?: any;
  userSource: string;
  tagId?: Tag[];
};

export interface Logs {
  _id: string;
  id: string;
  accountId: string;
  referenceId: string;
  source: string;
  actor?: any;
  action: string;
  meta: any;
  prevMeta?: any;
  createdAt: string;
}

export interface Timezone {
  description: string;
  name: string;
  value: string;
}

export interface ApiError {
  status: HttpStatus;
  data: {
    [key: string]: any;
    message: string;
  };
}

export interface Session {
  _id: string;
  id: string;
  userAgent: string;
  userId: string;
  device: string;
  jobTitle: string;
  lastLoggedInAt: string;
  parseLastLoggedInAt?: string;
  status?: string;
  createdAt: string;
  updatedAt: string;
  active?: boolean;
}

export interface ACCOUNT_USER {
  _id: string;
  id: string;
  accountId: string;
  photoUrl: string;
  firstName: string;
  lastName: string;
  fullName: string;
  emailAddress: string;
  passwordChangedAt: string;
  isEmailAddressVerified: string;
  landlineNumber: string;
  isMfaEnabled: boolean;
  roleId: any;
  source: string;
  status?: string;
  createdAt: string;
  updatedAt: string;
}

export interface Permission {
  _id: string;
  accountId: string;
  permissionId: {
    _id: string;
    key: PERMISSION_KEYS;
    module: ROLE_MODULE;
    name: string;
  };
  roleId: string;
  status: STATUS;
  actions: string[];
  accessLevel: string;
}

export interface Role {
  _id: string;
  name: string;
  key: string;
  accountId: string;
  rolePermissions: Permission[];
  createdAt: string;
  updatedAt: string;
  isDeletable: boolean;
}

export type Booking = {
  _id: string;
  accountUserId: ACCOUNT_USER;
  accountId: string;
  title: string;
  eventUrl: string;
  expiresAt?: string;
  duration: number;
  notifyBefore: number;
  content?: string;
  status: string;
  timezone: string;
};

export enum ACCOUNT_CARD_COLOR {
  '#3DB512' = '#3DB512',
  '#F55A58' = '#F55A58',
  '#FC9342' = '#FC9342',
  '#F5BA02' = '#F5BA02',
  '#C9A357' = '#C9A357',
  '#2349E5' = '#2349E5',
  '#8300E8' = '#8300E8',
  '#312F3B' = '#312F3B',
}

export type AccountCardMeta = {
  firstName: string;
  lastName: string;
  fullName: string;
  emailAddress: string;
  photoUrl?: string;
  jobTitle?: string;
  jobDepartment?: string;
  companyName?: string;
  companyLogoUrl?: string;
  address?: string;
  mobileNumber?: string;
  website?: string;
  linkedinUrl?: string;
  facebookUrl?: string;
  instagramUrl?: string;
  xUrl?: string;
  color?: ACCOUNT_CARD_COLOR;
};

export type AccountCard = {
  _id: string;
  accountUserId: ACCOUNT_USER;
  accountId: string;
  qrCodeUrl: string;
  vCardUrl: string;
  meta: AccountCardMeta;
  isDefault: string;
};

export type TOKEN_SCOPE_TYPE = 'EMAIL' | 'MEETING' | 'INBOX';

export type TokenMeta = {
  emailAddress?: string;
  scopes: TOKEN_SCOPE_TYPE[];
  [key: string]: any;
};
export type Token = {
  _id: string;
  accountId: string;
  accountUserId: string;
  partnerId: string;
  accessToken: string;
  refreshToken: string;
  source: string;
  status: string;
  expiresAt?: number;
  emailAddress?: string;
  meta?: TokenMeta;
};

// SMS Types Start
export enum SMS_STATUS_TYPE {
  PENDING = 'PENDING',
  SENT = 'SENT',
  FAILED = 'FAILED',
  RECEIVED = 'RECEIVED',
}

export enum ACTIVE_NUMBER_SOURCE {
  GENERAL = 'GENERAL',
  ACCOUNT = 'ACCOUNT',
  ACCOUNT_USER = 'ACCOUNT_USER',
}

export enum SMS_TYPE {
  LEAD = 'LEAD',
  ACCOUNT = 'ACCOUNT',
  GENERAL = 'GENERAL',
}

export enum SMS_ACTOR_SOURCE {
  GENERAL = 'GENERAL',
  ACCOUNT = 'ACCOUNT',
  ACCOUNT_USER = 'ACCOUNT_USER',
  LEAD = 'LEAD',
  UNKNOWN = 'UNKNOWN',
}

export type SMS = {
  _id: string;
  actor: {
    actorId: string;
    meta: ACCOUNT_USER;
    role: string;
  };
  from: string;
  fromId: string;
  fromSource: SMS_ACTOR_SOURCE;
  to: string;
  toId?: Lead | null;
  toSource: SMS_ACTOR_SOURCE;
  body: string;
  status: SMS_STATUS_TYPE;
  type: SMS_TYPE;
  messageId: string;
  isRead: boolean;
  isArchived: boolean;
  createdAt: string;
  updatedAt: string;
  smsConversationId: string;
};

export type SMSConversation = {
  _id: string;
  from: string;
  fromId: string;
  to: string;
  toId?: Lead | null;
  toName?: string | null;
  fromSource: SMS_ACTOR_SOURCE;
  toSource: SMS_ACTOR_SOURCE;
  type: SMS_TYPE;
  sms: SMS[];
  createdAt: string;
  updatedAt: string;
  isRead: boolean;
};

export type SendSMSInput = {
  body: string;
  receiverType: SMS_ACTOR_SOURCE | SMS_TYPE;
  senderType: SMS_ACTOR_SOURCE | SMS_TYPE;
  type: SMS_TYPE;
  mobileNumber?: string;
  referenceId?: string;
  accountId?: string;
};

export type SMSAssignToReference = {
  receiverType: ACTIVE_NUMBER_SOURCE | SMS_TYPE;
  senderType: ACTIVE_NUMBER_SOURCE | SMS_TYPE;
  mobileNumber: string;
  referenceId: string;
  accountId?: string;
};

export type SMSPlan = {
  _id: string;
  accountId: string;
  balance: number;
  pendingBalance: number;
  inboundBalance: number;
  outboundBalance: number;
  inboundTotalBalance: number;
  outboundTotalBalance: number;
  status: SMS_STATUS_TYPE;
  createdAt: string;
  updatedAt: string;
  __v: number;
  id: string;
  activeNumber: ActiveNumber;
  allowExcessSms: boolean;
};

type ActiveNumber = {
  _id: string;
  referenceId: string;
  source: SMS_ACTOR_SOURCE;
  name: string;
  number: string;
  status: SMS_STATUS_TYPE;
  createdAt: string;
  updatedAt: string;
};

// SMS Types End

export type GmailThreads = {
  id: string;
  snippet: string;
  historyId: string;
}[]; // Type return when fetching a list of threads

export type GmailThread = {
  id: string;
  historyId: string;
  messages: Message[];
};

export type Message = {
  id: string;
  threadId: string;
  labelIds: string[];
  snippet: string;
  payload: MessagePart;
  sizeEstimate: number;
  historyId: string;
  internalDate: string;
};

export type MessagePart = {
  mimeType: string;
  headers: Header[];
  parts: MessagePart[];
  filename: string;
  body: { size: number; attachmentId: string; data: string };
};

export type Header = {
  name: string;
  value: string;
};

export type AutomationModuleKeys =
  | 'LEADS'
  | 'DEALS'
  | 'EVENTS'
  | 'TASKS'
  | 'ORDERS';

export interface AutomationWorkflow {
  type: 'CONDITION' | 'ACTION' | 'DELAY' | '';
  meta: any;
  key?: string;
  status?: STATUS;
}

export interface Automation {
  module: AutomationModuleKeys;
  title: string;
  description: string;
  trigger: string;
  workflow?: AutomationWorkflow[];
  _id: string;
  status: STATUS;
  createdAt: string;
  note?: string;
  type?: 'CUSTOM' | 'DEFAULT';
  key?: string;
}

export interface AutomationTemplate
  extends Omit<Automation, '_id' | 'status' | 'createdAt'> {
  templateComponent?: (automation?: AutomationTemplate) => JSX.Element;
  requiresGmail?: boolean;
  requiresSendgrid?: boolean;
  _id?: string;
}

export interface Tag {
  key: string;
  tag: string;
  _id: string;
  createdAt?: string;
  updatedAt?: string;
  isDeletable?: boolean;
}

export interface Ticket {
  _id: string;
  description: string;
  subject: string;
  type: TICKET_TYPE;
  status: TICKET_STATUS;
  createdAt: string;
  updatedAt: string;
  accountUserId: ACCOUNT_USER;
  accountId: Account;
  attachments?: Attachment[];
  comments?: Comment[];
}

export interface Profile {
  _id: string;
  accountId: string;
  emailAddress: string;
  firstName: string;
  lastName: string;
  id: string;
  fullName: string;
  photoUrl?: string;
  isMfaEnabled?: string;
  landlineNumber?: string;
  passwordChangedAt?: string;
  recoveryMobileNumber?: string;
  mobileNumber?: string;
  createdAt: string;
  roleId: string;
}

export interface View {
  name: string;
  description: string;
  meta: Record<string, any>;
  source: string;
  isPrimary: boolean;
  _id: string;
  createdAt?: string;
  updatedAt?: string;
}

export type OutlookMessagesResult = {
  '@odata.context': string;
  value: OutlookMessage[];
  '@odata.nextLink': string;
};

export type OutlookMessage = {
  '@odata.etag': string;
  id: string;
  receivedDateTime: string;
  sentDateTime: string;
  subject: string;
  isRead: boolean;
  bodyPreview: string;
  hasAttachments: boolean;
  from: OutlookMessageFrom;
  conversationId: string;
  body?: {
    contentType: string;
    content: string;
  };
  toRecipients?: OutlookMessageFrom[];
  ccRecipients?: OutlookMessageFrom[];
  bccRecipients?: OutlookMessageFrom[];
};

export type OutlookMessageFrom = {
  emailAddress: {
    name: string;
    address: string;
  };
};

export type OutlookMessageAttachmentsResult = {
  '@odata.context': string;
  value: OutlookMessageAttachment[];
};

export interface OutlookMessageAttachment {
  '@odata.type': string;
  '@odata.mediaContentType': string;
  id: string;
  lastModifiedDateTime: string;
  name: string;
  contentType: string;
  size: number;
  isInline: boolean;
  contentId: string;
  contentLocation: null;
  contentBytes: string;
}

export enum DATA_TYPE {
  STRING = 'STRING',
  MOBILENUMBER = 'MOBILENUMBER',
  NUMBER = 'NUMBER',
  BOOLEAN = 'BOOLEAN',
  DATE = 'DATE',
  SELECT = 'SELECT',
  MULTISELECT = 'MULTISELECT',
  CHECKBOX = 'CHECKBOX',
  RADIOBOX = 'RADIOBOX',
}

export enum DATA_SOURCE {
  LEAD = 'LEAD',
  COMPANY = 'COMPANY',
  DEAL = 'DEAL',
  TASK = 'TASK',
}

export enum DATA_NUMBER_FORMAT {
  FORMATTED = 'FORMATTED',
  UNFORMATTED = 'UNFORMATTED',
  CURRENCY = 'CURRENCY',
  PERCENTAGE = 'PERCENTAGE',
}

export enum DATA_DATE_FORMAT {
  DATE = 'DATE',
  DATETIME = 'DATETIME',
}

export enum DATA_STRING_FORMAT {
  STRING = 'STRING',
  LONGSTRING = 'LONGSTRING',
}

export type Data = {
  _id: string;
  accountId: string;
  name: string;
  description?: string;
  type: DATA_TYPE;
  source: DATA_SOURCE;
  isRequired?: boolean;
  createdAt: string;
  updatedAt: string;
  totalAssociatedId: number;
  meta: {
    options?: { label: string; value: string }[];
    format?: string;
    min?: number;
    max?: number;
  };
};

export enum TICKET_TYPE {
  GENERAL = 'GENERAL',
  TECHNICAL_ISSUE = 'TECHNICAL_ISSUE',
  BILLING = 'BILLING',
}

export enum TICKET_STATUS {
  OPEN = 'OPEN',
  IN_PROGRESS = 'IN_PROGRESS',
  CLOSED = 'CLOSED',
}

export enum VIEWDATA_TYPE {
  TABLE = 'TABLE',
  INFO_PAGE = 'INFO_PAGE',
  FORM = 'FORM',
}

export type VIEWDATA_DATA = {
  dataId: string;
  sequence?: number;
};

export type ViewData = {
  _id: string;
  accountId: string;
  data: VIEWDATA_DATA[];
  source: DATA_SOURCE;
  type: VIEWDATA_TYPE;
  dataList: Pick<Data, '_id' | 'description' | 'meta' | 'name' | 'type'>[];
};

export type DataRecord = {
  _id: string;
  accountId: string;
  name: string;
  source: DATA_RECORD_SOURCE;
  action: DATA_RECORD_ACTION;
  status: DATA_RECORD_STATUS;
  summary?: DataRecordSummary;
  errors?: DataRecordRows[];
  fileUrl?: string;
  createdAt: string;
};

export enum DATA_RECORD_SOURCE {
  LEAD = 'LEAD',
  COMPANY = 'COMPANY',
  DEAL = 'DEAL',
}

export enum DATA_RECORD_ACTION {
  IMPORT = 'IMPORT',
  EXPORT = 'EXPORT',
}

export enum DATA_RECORD_STATUS {
  PENDING = 'PENDING',
  INPROGRESS = 'INPROGRESS',
  COMPLETED = 'COMPLETED',
  FAILED = 'FAILED',
}

export type DataRecordSummary = {
  totalRecords: number;
  validRecords: number;
  errorRecords: number;
  failedRecords?: number;
  successRecords?: number;
};

export type DataRecordRows = {
  message?: string;
  fieldName?: string;
  value?: string;
  row?: number;
  column?: number;
};

export type DataRecordValidateSummary = {
  summary: DataRecordSummary;
  errors: DataRecordRows[];
  records: DataRecordRows[];
};

export enum EMAIL_TEMPLATE_VISIBILITY {
  PRIVATE = 'PRIVATE',
  EVERYONE = 'EVERYONE',
}

export type EmailTemplate = {
  _id: string;
  accountId: string;
  accountUserId: string | ACCOUNT_USER;
  name: string;
  subject: string;
  body: string;
  isShared: boolean;
  createdAt: string;
  updatedAt: string;
};

export interface ToadMessage {
  role: CHAT_ROLE;
  content: string;
  createdAt?: string;
}

export type Conversation = {
  _id: string;
  title: string;
  createdAt: string;
  udpatedAt: string;
  accountId: string;
  accountUserId: string;
};

export enum CONVERSATION_CONTENT_ROLE {
  assistant = 'assistant',
  user = 'user',
  system = 'system',
}

export type REPORT_TYPE =
  | 'lead-summary'
  | 'sales-pipeline-report'
  | 'deals-won-lost'
  | 'top-performing-users'
  | 'activity-report';

export interface CreateDataReportResponse {
  accountId: string;
  accountUserId: string;
  name: string;
  key: string;
  source: string;
  status: string;
  meta: DataReportMeta[];
  _id: string;
  createdAt: string;
  updatedAt: string;
  __v: number;
}

interface DataReportMeta {
  type: string;
  source: string;
  parameters: Params;
  name: string;
}

interface Params {
  startDate: string;
  endDate: string;
  meta: ParametersMeta[];
  query: string;
}

interface ParametersMeta {
  condition: string;
  operand: string;
  operator: string;
  result: string;
}

export enum DATA_STATUS {
  PENDING = 'PENDING',
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED',
  FAILED = 'FAILED',
}

export type DataReportListItem = {
  _id: string;
  accountId: string;
  accountUserId: string;
  name: string;
  key: string;
  source: string;
  status: DATA_STATUS;
  createdAt: string;
  updatedAt: string;
  __v: number;
};
export type DataReportListResponse = DataReportListItem[];

export interface DataReportMetaParameters {
  startDate: string;
  endDate: string;
  meta: ParametersMeta[];
  query?: { [key: string]: any };
  metaQuery?: Record<string, any>;
}

export interface DataReportDetail {
  _id: string;
  accountId: string;
  accountUserId: string;
  name: string;
  source: string;
  status: string;
  meta: {
    name: string;
    type: string;
    source: string;
    parameters: DataReportMetaParameters;
    _id: string;
    status: string;
    value: {
      [key: string]: any;
    };
  }[];
}

export type SETUP_STEP = {
  step1?: boolean;
  step2?: boolean;
  step3?: boolean;
  step4?: boolean;
  step5?: boolean;
  step6?: boolean;
};

export interface Timezones {
  zoneName: string;
  gmtOffset: number;
  gmtOffsetName: string;
  abbreviation: string;
  tzName: string;
}
export interface ICountry {
  name: string;
  phonecode: string;
  isoCode: string;
  flag: string;
  currency: string;
  latitude: string;
  longitude: string;
  timezones?: Timezones[];
}

export interface IState {
  name: string;
  isoCode: string;
  countryCode: string;
  latitude?: string | null;
  longitude?: string | null;
}
export interface ICity {
  name: string;
  countryCode: string;
  stateCode: string;
  latitude?: string | null;
  longitude?: string | null;
}

export interface AICallerVoice {
  name: string;
  voiceId: string;
  previewUrl: string;
  description: string;
}

export interface AgentKnowledgeBase {
  name: string;
  id: string;
  metadata: {
    createdAtUnixSecs: number;
    lastUpdatedAtUnixSecs: number;
  };
  extractedInnerHtml: string;
}

export interface AICallerAgent {
  _id: string;
  agentPhoneNumebrId: string;
  phoneNumberId: string;
  phoneNumber: string;
  agentId: string;
  voiceId?: string;
  knowledgeBaseId: string[];
  mcpId: string[];
  defaultPrompt: string;
  prompt: string;
  firstMessage: string;
  createdAt: string;
  updatedAt: string;
  status: STATUS;
}

export interface AICampaign {
  _id: string;
  type: string;
  status: CAMPAIGN_STATUS_TYPE;
  name: string;
  description?: string;
  scheduledAt: string;
  createdAt: string;
  updatedAt: string;
  meta: {
    payload: {
      firstMessage: string;
      prompt: string;
    };
    summary: {
      total: number;
      totalSent: number;
      totalFailed: number;
    };
  };
}

export interface CampaignMessage {
  _id: string;
  campaignId: string;
  recipient: string;
  status: CAMPAIGN_MESSAGE_STATUS_TYPE;
  type: 'CALL';
  processedAt: string;
  meta: Record<string, any>;
  payload: Record<string, any>;
}

export interface AICallLog {
  agentId: string;
  calledAt: string;
  conversationId: string;
  createdAt: string;
  duration: number;
  from: string;
  id: string;
  lead?: Lead;
  leadId?: string;
  to: string;
  type: 'OUTBOUND' | 'INBOUND';
}

export interface AICallLogMessage {
  agentCallId: string;
  accountId: string;
  createdAt: string;
  meta: {
    role: 'agent' | 'user';
    message: string;
  };
}
